import { z } from "zod";
import {
  DEFAULT_CALCULATION_SORT,
  DEFAULT_WIDGET_DISPLAY_OPTIONS,
  REPORT_SCHEMA_VERSION,
  type DimensionKey,
  type StudioReport,
  type VisualisationType,
} from "./report-definition";
import {
  canonicalDateSchema,
  parseCurrentStudioReport,
  type StudioReportParseResult,
} from "./report-schema";

type UnknownRecord = Record<string, unknown>;

export interface ReportMigrationContext {
  /** Required when upgrading the former owner-less SavedReport shape. */
  readonly ownerId?: string;
  readonly now?: string;
  readonly idFactory?: () => string;
}

export class ReportMigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ReportMigrationError";
  }
}

export class UnsupportedReportSchemaVersionError extends ReportMigrationError {
  readonly schemaVersion: number;

  constructor(schemaVersion: number) {
    super(`Report schema version ${schemaVersion} is not supported by this version of Studio.`);
    this.name = "UnsupportedReportSchemaVersionError";
    this.schemaVersion = schemaVersion;
  }
}

const LEGACY_DIMENSIONS = {
  specialty: "specialty",
  asa: "asa",
  urgency: "urgency",
  age: "age_group",
  anaesthetic: "anaesthetic_technique",
  airway: "airway_technique",
  region: "regional_technique",
  procedure: "procedure",
  proceduralActivity: "practical_procedure",
  hospital: "hospital",
  supervisorGrade: "supervisor_grade",
  supervision: "supervision_level",
} as const satisfies Readonly<Record<string, DimensionKey>>;

export type LegacySavedReportDimension = keyof typeof LEGACY_DIMENSIONS;

export interface LegacySavedReport {
  readonly id: string;
  readonly name: string;
  readonly dimension: LegacySavedReportDimension;
  readonly startDate: string;
  readonly endDate: string;
  readonly createdAt: string;
}

function record(value: unknown): UnknownRecord {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as UnknownRecord
    : {};
}

function defaultIdFactory(): string {
  return globalThis.crypto.randomUUID();
}

function nowFrom(context: ReportMigrationContext): string {
  return context.now ?? new Date().toISOString();
}

function timestampOrFallback(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const parsed = z.iso.datetime({ offset: true }).safeParse(value);
  return parsed.success ? parsed.data : fallback;
}

function optionalLegacyDateRange(raw: UnknownRecord): { startDate: string; endDate: string } | undefined {
  const startDate = typeof raw.startDate === "string" ? raw.startDate : "";
  const endDate = typeof raw.endDate === "string" ? raw.endDate : "";
  if (!startDate && !endDate) return undefined;
  if (!startDate || !endDate) {
    throw new ReportMigrationError("The saved report has an incomplete date range and cannot be upgraded safely.");
  }
  const start = canonicalDateSchema.safeParse(startDate);
  const end = canonicalDateSchema.safeParse(endDate);
  if (!start.success || !end.success || start.data > end.data) {
    throw new ReportMigrationError("The saved report has an invalid date range and cannot be upgraded safely.");
  }
  return { startDate: start.data, endDate: end.data };
}

function legacyDimension(value: unknown): DimensionKey | null {
  if (typeof value !== "string" || !Object.hasOwn(LEGACY_DIMENSIONS, value)) return null;
  return LEGACY_DIMENSIONS[value as LegacySavedReportDimension];
}

function labelForDimension(value: DimensionKey): string {
  const labels: Readonly<Partial<Record<DimensionKey, string>>> = {
    specialty: "specialty",
    asa: "ASA",
    urgency: "urgency",
    age_group: "age group",
    anaesthetic_technique: "anaesthetic technique",
    airway_technique: "airway technique",
    regional_technique: "regional technique",
    procedure: "procedure",
    practical_procedure: "practical procedure",
    hospital: "hospital",
    supervisor_grade: "supervisor grade",
    supervision_level: "supervision level",
  };
  return labels[value] ?? value.replaceAll("_", " ");
}

function legacyVisualisation(_dimension: DimensionKey): VisualisationType {
  return "horizontal_bar";
}

export function isLegacySavedReport(value: unknown): value is LegacySavedReport {
  const raw = record(value);
  return raw.schemaVersion === undefined
    && typeof raw.id === "string"
    && raw.id.trim() !== ""
    && typeof raw.name === "string"
    && raw.name.trim() !== ""
    && legacyDimension(raw.dimension) !== null
    && typeof raw.startDate === "string"
    && typeof raw.endDate === "string"
    && typeof raw.createdAt === "string";
}

export function migrateLegacySavedReport(
  value: LegacySavedReport,
  context: ReportMigrationContext,
): StudioReport {
  const ownerId = context.ownerId?.trim();
  if (!ownerId) {
    throw new ReportMigrationError("An authenticated owner is required to upgrade this saved report.");
  }
  const raw = record(value);
  const dimension = legacyDimension(value.dimension);
  if (dimension === null) {
    throw new ReportMigrationError(`The saved report uses an unsupported dimension: ${String(value.dimension)}.`);
  }
  const idFactory = context.idFactory ?? defaultIdFactory;
  const widgetId = idFactory();
  const now = nowFrom(context);
  const createdAt = timestampOrFallback(value.createdAt, now);
  const dateRange = optionalLegacyDateRange(raw);
  const dimensionLabel = labelForDimension(dimension);

  return parseCurrentStudioReport({
    id: value.id.trim(),
    ownerId,
    name: value.name.trim(),
    schemaVersion: REPORT_SCHEMA_VERSION,
    dataSource: "cases",
    globalFilters: [],
    ...(dateRange ? { dateRange } : {}),
    widgets: [{
      id: widgetId,
      type: "chart",
      title: `Cases by ${dimensionLabel}`,
      dataSource: "cases",
      filters: [],
      calculation: {
        metric: "case_count",
        primaryDimension: dimension,
        percentageDenominator: "included_cases",
        sort: DEFAULT_CALCULATION_SORT,
        includeEmptyCategories: false,
      },
      visualisation: { type: legacyVisualisation(dimension) },
      displayOptions: DEFAULT_WIDGET_DISPLAY_OPTIONS,
    }],
    layout: [{
      widgetId,
      x: 0,
      y: 0,
      width: 12,
      height: 7,
      minimumWidth: 4,
      minimumHeight: 4,
    }],
    createdAt,
    updatedAt: createdAt,
  });
}

/**
 * Runs structural migrations but leaves final validation to the current schema.
 * It deliberately rejects unknown future versions rather than guessing.
 */
export function migrateReportDefinition(
  value: unknown,
  context: ReportMigrationContext = {},
): unknown {
  const raw = record(value);
  if (raw.schemaVersion === REPORT_SCHEMA_VERSION) {
    if ((raw.ownerId === undefined || raw.ownerId === "") && context.ownerId?.trim()) {
      return { ...raw, ownerId: context.ownerId.trim() };
    }
    return value;
  }

  if (typeof raw.schemaVersion === "number") {
    throw new UnsupportedReportSchemaVersionError(raw.schemaVersion);
  }

  if (isLegacySavedReport(value)) return migrateLegacySavedReport(value, context);

  throw new ReportMigrationError("The saved report is malformed or has no recognised schema version.");
}

export function parseStudioReport(
  value: unknown,
  context: ReportMigrationContext = {},
): StudioReport {
  return parseCurrentStudioReport(migrateReportDefinition(value, context));
}

export type MigratedStudioReportParseResult =
  | StudioReportParseResult
  | { readonly success: false; readonly error: ReportMigrationError };

export function safeParseStudioReport(
  value: unknown,
  context: ReportMigrationContext = {},
): MigratedStudioReportParseResult {
  try {
    return { success: true, data: parseStudioReport(value, context) };
  } catch (reason) {
    if (reason instanceof z.ZodError || reason instanceof ReportMigrationError) {
      return { success: false, error: reason };
    }
    throw reason;
  }
}
